import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = "/api";
const AUTH_KEY = "dslr_access_token";
const REFRESH_KEY = "dslr_refresh_token";
const AuthContext = createContext(null);

// Attach the current token as a Bearer header on every request.
// This is a fallback for browsers that block cross-site or third-party
// cookies (Safari ITP, Firefox ETP, Brave, etc.) on the preview domain.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_KEY);
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

function persistTokens(data) {
  if (data?.access_token) localStorage.setItem(AUTH_KEY, data.access_token);
  if (data?.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
}

function clearTokens() {
  localStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          originalRequest.url !== `${API}/auth/me` &&
          originalRequest.url !== `${API}/auth/refresh`
        ) {
          originalRequest._retry = true;
          try {
            const refreshToken = localStorage.getItem(REFRESH_KEY);
            const { data } = await axios.post(
              `${API}/auth/refresh`,
              refreshToken ? { refresh_token: refreshToken } : {},
              { withCredentials: true }
            );
            if (data?.access_token) {
              localStorage.setItem(AUTH_KEY, data.access_token);
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
            }
            return axios(originalRequest);
          } catch {
            clearTokens();
            setUser(false);
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    persistTokens(data);
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(
      `${API}/auth/register`,
      { name, email, password },
      { withCredentials: true }
    );
    persistTokens(data);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
